import WorkbenchWelcome from '../workbench/WorkbenchWelcome.jsx';
import { initialCollaborationText } from '../../lib/composer/collaborationMode.js';
// Apolo IDE — CodeView (Code tab root, home + session-active switch).
//
// Epic 4: renders the home screen (welcome + PRs + prompt cards + composer
// + footer). When a session is active (selected via CodeSidebar), Epic 5
// will swap this body for the SessionWorkspace.

import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore.js';
import { useAgentStore } from '../../store/useAgentStore.js';
import './cursor-chrome.css';
import CodeSidebar from './CodeSidebar.jsx';
import WorkbenchSidebar from '../workbench/WorkbenchSidebar.jsx';
import ChatHomeComposer from '../chat/workspace/ChatHomeComposer.jsx';
import { chatTurnTextWithAttachments } from '../../hooks/useFileAttachments.js';
import CodeComposer from './CodeComposer.jsx';
import SessionWorkspace from './SessionWorkspace.jsx';
import WorkspacePaneLayout from './WorkspacePaneLayout.jsx';
import PreviewPanel from './PreviewPanel.jsx';
import MobileDrawer from '../MobileDrawer.jsx';
// FASE-11 E5-WS4-T01 — as três views pesadas do Code montam SÓ sob demanda
// (activeView), então carregam sob demanda: React.lazy tira o peso delas (e das
// suas árvores de deps) do chunk de boot. Padrão já provado no ChartRenderer.
const SchedulesView = lazy(() => import('../schedules/SchedulesView.jsx'));
const KanbanBoard = lazy(() => import('../kanban/KanbanBoard.jsx'));
const CodeMap = lazy(() => import('./code-map/CodeMap.jsx'));
import SupabaseProvisionModal from '../integrations/SupabaseProvisionModal.jsx';
import ConnectionsModal from '../integrations/ConnectionsModal.jsx';
import { buildNewChatIntent, intentToSessionInput } from '../../lib/composer/newChatIntent.js';

export default function CodeView({ unified = false }) {
  const [newChatRequest, setNewChatRequest] = useState(null);
  const draftIdentity = useAuthStore((state) => `${state.user?.id || 'anonymous'}:${state.activeProfileId || 'default'}`);
  const surface = useAgentStore((s) => s.code.ui.workbenchSurface || 'chat');
  const chatHome = unified && surface === 'chat';
  const Sidebar = unified ? WorkbenchSidebar : CodeSidebar;
  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const workspacePanes = useAgentStore((s) => s.code.workspacePanes);
  const previewPanelOpen = useAgentStore((s) => Boolean(s.code.ui.previewPanelOpen));
  const setPreviewPanelOpen = useAgentStore((s) => s.setPreviewPanelOpen);
  const sessions = useAgentStore((s) => s.code.sessions);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLg, setIsLg] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  // Sprint v9.21 — activeView controla qual screen renderiza no main:
  // 'home' (default) | 'schedules'. Sidebar dispatcha apolo:setCodeView.
  const [activeView, setActiveView] = useState('home');
  useEffect(() => { const show = () => setActiveView('home'); window.addEventListener('apolo:workbenchSurface', show); return () => window.removeEventListener('apolo:workbenchSurface', show); }, []);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsLg(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Sprint v9.4 — hamburger global do AppShell dispara este evento.
  useEffect(() => {
    function onOpenDrawer() {
      setDrawerOpen(true);
    }
    window.addEventListener('apolo:openMobileDrawer', onOpenDrawer);
    return () => window.removeEventListener('apolo:openMobileDrawer', onOpenDrawer);
  }, []);

  // Sprint v11 — adicionado 'sprints'
  useEffect(() => {
    function onSetView(e) {
      const v = e?.detail?.view;
      if (v === 'home' || v === 'schedules' || v === 'sprints' || v === 'codemap') {
        setActiveView(v);
        const { activeSessionId, workspacePanes } = useAgentStore.getState().code;
        if (activeSessionId || workspacePanes.length > 0) {
          useAgentStore.getState().setActiveSession(null);
        }
      }
    }
    window.addEventListener('apolo:setCodeView', onSetView);
    return () => window.removeEventListener('apolo:setCodeView', onSetView);
  }, []);

  useEffect(() => {
    if (!unified) return;
    const onNew = (event) => {
      const store = useAgentStore.getState();
      const current = store.code.sessions.find((s) => s.id === store.code.activeSessionId);
      const explicit = event?.detail;
      const draft = store.code.newChatContext?.scope === draftIdentity ? store.code.newChatContext : null;
      const hasExplicitProject = explicit && Object.hasOwn(explicit, 'project');
      const directory = hasExplicitProject ? explicit.project?.root || null : current ? current.directory || null : draft?.directory || null;
      const github_repo = hasExplicitProject || directory ? null : current ? current.github_repo || null : draft?.github_repo || null;
      setNewChatRequest({ id: crypto.randomUUID(), directory, github_repo });
      useAgentStore.getState().setActiveWorkspace('code');
      useAgentStore.getState().setActiveSession(null);
      setActiveView('home');

    };
    window.addEventListener('apolo:newSession', onNew);
    return () => window.removeEventListener('apolo:newSession', onNew);
  }, [unified, draftIdentity]);

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null;
  const hasWorkspacePanes = workspacePanes.length > 0;
  const hasWorkspaceSession = Boolean(activeSessionId) || hasWorkspacePanes;
  const showMultiPane = hasWorkspacePanes && isLg;
  const showSessionWorkspace = activeSession && (!hasWorkspacePanes || !isLg);
  const showPreviewRail =
    previewPanelOpen && hasWorkspaceSession && isLg && (showMultiPane || showSessionWorkspace);

  // BUGFIX (Mapa do Código): a view do mapa é session-less (top-level), então
  // precisa de um repo root real. Antes o CodeMap lia window.apolo.cwd() (que
  // nunca existiu) e nunca conectava. Aqui derivamos o root da sessão ativa, ou
  // da sessão mais recente com um diretório (worktree). null → empty-state claro.
  const codeMapRoot =
    activeSession?.directory ||
    sessions.find((s) => typeof s.directory === 'string' && s.directory.startsWith('/'))?.directory ||
    null;

  function openNewChat(detail) {
    if (unified) { window.dispatchEvent(new CustomEvent('apolo:newSession', { detail })); return; }
    useAgentStore.getState().setActiveSession(null);
    setActiveView('home');
    window.dispatchEvent(new CustomEvent(unified ? 'apolo:chatNewSession' : 'apolo:focusCodeComposer'));
  }

  async function handleComposerSubmit({ text, model, permissions_mode, github_repo, directory, attachments, client_intent_id, mode: collaborationMode, environment_mode }) {
    // Home composer cria a sessão E envia a primeira mensagem pro agente.
    // createCodeSession já ativa a sessão (activeSessionId), abrindo o
    // SessionWorkspace. v10.7: armamos pendingFirstMessage pra o workspace
    // auto-enviar no mount (antes a msg só virava o nome da sessão = silêncio).
    if (!text) return;
    const intent = buildNewChatIntent({
      text,
      client_intent_id,
      kind: chatHome ? 'chat' : 'code',
      mode: chatHome ? (environment_mode || 'direct') : useAgentStore.getState().code.ui.mode,
      permissions_mode,
      model,
      github_repo,
      directory,
      attachments,
    });
    const initialText = initialCollaborationText(collaborationMode, chatTurnTextWithAttachments(true, text, attachments));
    const res = await useAgentStore.getState().createCodeSession({
      name: text.length > 60 ? `${text.slice(0, 57)}…` : text,
      description: github_repo
        ? `${text}\n\nRepositório GitHub: ${github_repo.full_name} (${github_repo.url || ''})`
        : text,
      ...intentToSessionInput(intent),
      ...(client_intent_id ? { initial_message: { content: initialText, draft_scope: `intent:${client_intent_id}`, attachment_count: attachments?.length || 0 }, flags: { collaboration_mode: collaborationMode || 'chat', ...(['ask', 'research'].includes(collaborationMode) ? { read_only: true } : {}) } } : {}),
      // D5: o repo é resolvido pra um checkout local + worktree determinístico
      // dentro do createCodeSession (não mais via mensagem de clone pro LLM).
      ...(github_repo?.default_branch ? { branch: github_repo.default_branch } : {}),
    });
    if (res.inactiveProfile) return res;
    if (res.ok && res.session) {
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'success', message: 'Sessão criada.' },
      }));
      // D5: o repo já foi clonado/resolvido pra um checkout local e o worktree
      // aponta pra ele — então a primeira mensagem é só a tarefa, sem o clone
      // frágil injetado pro LLM.
      if (collaborationMode) useAgentStore.getState().setChatMode(res.session.id, collaborationMode);
      useAgentStore.getState().setPendingFirstMessage(res.session.id, initialText, attachments, Boolean(client_intent_id));
      // Consome a intenção pontual: depois de criar a sessão a partir de um repo
      // escolhido, limpa a seleção e volta o scope pro default. Sem isso o repo
      // (ex.: glyco-company) ficava grudado e TODA sessão nova reabria o mesmo
      // projeto via o clone injetado acima.
      if (github_repo) {
        const store = useAgentStore.getState();
        store.setUiPref('github_repo', null);
        store.setUiPref('scope', 'local');
      }
    } else {
      // v10.7: nunca mais silêncio — se a criação falhar, mostra o motivo.
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: { kind: 'error', message: `Falha ao criar sessão: ${res.error || 'erro desconhecido'}` },
      }));
    }
    return res;
  }

  const goHome = () => useAgentStore.getState().setActiveSession(null);

  return (
    <div data-surface={surface} className={`flex h-full w-full flex-1 ${unified ? 'workbench-shell' : ''}`} id="tabpanel-code" role="tabpanel" aria-labelledby="tab-code">
      {unified && !hasWorkspaceSession && <button aria-label="Abrir chats" className="absolute left-3 top-3 z-10 h-11 rounded-lg bg-apolo-surface1 px-3 lg:hidden" onClick={() => setDrawerOpen(true)}>☰ Chats</button>}
      {/* Desktop sidebar (lg+) */}
      <div className="hidden lg:flex">
        <Sidebar onNewSession={openNewChat} />
      </div>
      {/* Mobile drawer com o mesmo sidebar */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Sessões"
      >
        <Sidebar
          onNavigate={() => setDrawerOpen(false)}
          onNewSession={(detail) => {
            openNewChat(detail);
            setDrawerOpen(false);
          }}
        />
      </MobileDrawer>

      {showMultiPane ? (
        <div className="flex min-w-0 flex-1">
          <WorkspacePaneLayout />
          <PreviewPanel open={showPreviewRail} onClose={() => setPreviewPanelOpen(false)} />
        </div>
      ) : showSessionWorkspace ? (
        <div className="flex min-w-0 flex-1">
          <SessionWorkspace
            session={activeSession}
            onBack={goHome}
            onOpenSidebar={() => setDrawerOpen(true)}
          />
          <PreviewPanel open={showPreviewRail} onClose={() => setPreviewPanelOpen(false)} />
        </div>
      ) : activeView === 'schedules' ? (
        <main className="flex w-full max-w-full flex-1 flex-col gap-5 overflow-x-clip overflow-y-auto px-3 py-4 sm:gap-6 sm:px-6 sm:py-7">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-apolo-textPrimary">Agenda</h1>
            <p className="text-sm text-apolo-textTertiary">O que roda hoje e nos próximos dias.</p>
          </header>
          <Suspense fallback={<div className="p-6 text-sm text-apolo-textTertiary">Carregando…</div>}>
            <SchedulesView />
          </Suspense>
        </main>
      ) : activeView === 'sprints' ? (
        <main className="flex w-full max-w-full flex-1 flex-col gap-4 overflow-hidden px-3 py-4 sm:px-6 sm:py-6">
          <header className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-apolo-textPrimary">Kanban</h1>
            <span className="text-xs text-apolo-textTertiary">gerenciamento de demandas</span>
          </header>
          <div className="min-h-0 flex-1">
            <Suspense fallback={<div className="p-6 text-sm text-apolo-textTertiary">Carregando…</div>}>
              <KanbanBoard />
            </Suspense>
          </div>
        </main>
      ) : activeView === 'codemap' ? (
        <main className="flex w-full max-w-full flex-1 flex-col overflow-hidden">
          {/* Memory v2 MAP1/MAP2 — "Mapa do Código". onMention deep-links a node
              into the active chat by REUSING the existing apolo:insertComposer
              event (no new mechanism, no slash command). The composer resolves
              `@node:<id>` to the node's subgraph via /api/memory/retrieve. */}
          <Suspense fallback={<div className="p-6 text-sm text-apolo-textTertiary">Carregando…</div>}>
            <CodeMap
              repoRoot={codeMapRoot}
              onMention={(node) =>
                window.dispatchEvent(new CustomEvent('apolo:insertComposer', { detail: { text: `@node:${node.id} ` } }))
              }
            />
          </Suspense>
        </main>
      ) : (
        <main className="workbench-home relative flex w-full max-w-full flex-1 flex-col overflow-x-clip overflow-y-auto px-3 py-5 sm:px-6 sm:py-8">
          {/* Híbrido (#688 revisitado): full-width no laptop, mas com teto
              generoso centralizado (max-w-[1600px]) pra não esticar/esparsar
              em monitor ultralargo. As seções seguem w-full (preenchem o teto),
              então o guard de full-width continua válido. */}
          <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 sm:gap-7">
            <WorkbenchWelcome code={unified && surface === 'code'} />
            <section aria-label="Composer rápido" className="flex w-full min-w-0 flex-col gap-3">
              {chatHome ? <ChatHomeComposer unified key={draftIdentity} newChatRequest={newChatRequest} onNewChatHandled={(id) => setNewChatRequest((request) => request?.id === id ? null : request)} onSubmit={handleComposerSubmit} /> : <CodeComposer key={draftIdentity} newChatRequest={newChatRequest} onNewChatHandled={(id) => setNewChatRequest((request) => request?.id === id ? null : request)} onSubmit={handleComposerSubmit} primaryAction="Enviar" />}
            </section>
            <p className="mx-auto max-w-2xl text-center text-xs text-apolo-textTertiary">
              Use / para skills · @ para contexto · Enter para enviar
            </p>
          </div>
        </main>
      )}
      {/* Sprint v11 E13 — listens for apolo:openSupabaseProvision event */}
      <SupabaseProvisionModal />
      {/* Sprint v11+ — listens for apolo:openConnections event */}
      <ConnectionsModal />
    </div>
  );
}
